import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import { useContent } from "../context/ContentProvider";
import { ArrowRight, ArrowLeft, Loader2, Plus, Minus } from "lucide-react";
import { useRealtimeExtras } from "../hooks/useRealtimeExtras";
import { getFullImageUrl } from "../utils/imageUtils";

const ExtrasStep: React.FC = () => {
  const navigate = useNavigate();
  const { formData, selectedRooms, selectedExtras, updateSelectedExtras, setCurrentStep } =
    useBooking();
  const { content } = useContent();

  const [extraCounts, setExtraCounts] = useState<{ [key: string]: number }>({});

  // Real-time extras hook - uses selectedHotel from context
  const { extras, isLoading, error } = useRealtimeExtras();

  // Redirect if no rooms selected
  useEffect(() => {
    if (!formData.checkIn || !formData.checkOut || selectedRooms.length === 0) {
      navigate("../rooms");
      return;
    }

    // Initialize extra counts from selected extras
    const initialCounts: { [key: string]: number } = {};
    selectedExtras.forEach((extra) => {
      if ("quantity" in extra) {
        initialCounts[extra.id] = extra.quantity ?? 0;
      }
    });
    setExtraCounts(initialCounts);
  }, [formData, navigate, selectedRooms, selectedExtras]);

  const handleExtraCount = (extraId: string, increment: boolean) => {
    const currentCount = extraCounts[extraId] || 0;
    let newCount = increment ? currentCount + 1 : currentCount - 1;

    if (newCount < 0) newCount = 0;
    if (newCount > 10) newCount = 10; // Max 10 of each extra

    setExtraCounts({
      ...extraCounts,
      [extraId]: newCount,
    });
  };

  const handleBack = () => {
    navigate("../rooms");
  };

  const handleContinue = () => {
    const selected = extras
      .filter((extra) => (extraCounts[extra.id] || 0) > 0)
      .map((extra) => ({
        id: extra.id,
        name: extra.name,
        price: extra.price,
        quantity: extraCounts[extra.id] || 0,
        selected: true,
      }));

    updateSelectedExtras(selected);
    setCurrentStep(4);
  };

  const getTotalExtrasPrice = () => {
    return Object.keys(extraCounts).reduce((total, extraId) => {
      const extra = extras.find((e) => e.id === extraId);
      return total + (extra?.price || 0) * (extraCounts[extraId] || 0);
    }, 0);
  };

  // Calculate number of nights
  const nights =
    formData.checkIn && formData.checkOut
      ? Math.ceil(
          (formData.checkOut.getTime() - formData.checkIn.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  // Calculate rooms total
  const roomsTotal = selectedRooms.reduce(
    (sum, room) => sum + room.price * room.count * nights,
    0
  );

  // Group extras by category
  const groupedExtras = extras.reduce((groups, extra) => {
    const category = extra.category || "Autres";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(extra);
    return groups;
  }, {} as Record<string, typeof extras>);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
        <Loader2 className="w-12 h-12 text-[#67273f] animate-spin mb-4" />
        <h3 className="text-xl font-medium text-[#67273f]">
          Chargement des services...
        </h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-[10px]  text-center">
          <p className="font-medium">{error}</p>
          <button
            onClick={() => navigate("../rooms")}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-[10px]  hover:bg-red-700 transition-colors"
          >
            Retour aux chambres
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 bg-[#F5F3F0]">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold text-[#67273f] mb-6">
          {content.extrasTitle || "Services supplémentaires"}
        </h2>
        <p className="text-gray-600 mb-8">
          {content.extrasSubtitle || "Enrichissez votre séjour avec nos services exclusifs."}
        </p>

        {extras.length === 0 ? (
          <div className="bg-white p-8 rounded-[10px]  shadow-md text-center mb-8">
            <p className="text-gray-600">
              Aucun service supplémentaire disponible pour le moment.
            </p>
          </div>
        ) : (
          <div className="space-y-8 mb-8">
            {Object.entries(groupedExtras).map(([category, categoryExtras]) => (
              <div key={category}>
                {/* <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                  {category}
                </h3> */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categoryExtras.map((extra) => (
                    <div
                      key={extra.id}
                      className="bg-white rounded-[10px] shadow-md overflow-hidden"
                    >
                      {extra.image_url && (
                        <img
                          src={getFullImageUrl(extra.image_url)}
                          alt={extra.name}
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-[#67273f]">
                            {extra.name}
                          </h4>
                          <p className="text-lg font-bold text-[#67273f]">
                            {extra.price} €
                          </p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-gray-600 text-sm flex-1 mr-4">
                            {extra.description}
                          </p>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleExtraCount(extra.id, false)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F3F0] text-[#67273f] hover:bg-[#e2e0dd] transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>

                            <span className="w-8 text-center font-medium">
                              {extraCounts[extra.id] || 0}
                            </span>

                            <button
                              onClick={() => handleExtraCount(extra.id, true)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F3F0] text-[#67273f] hover:bg-[#e2e0dd] transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary and Navigation */}
        <div className="bg-white p-6 rounded-[10px]  shadow-md">
          <div className="flex flex-col space-y-3 mb-6">
            <div className="flex justify-between text-[#67273f]">
              <span>Chambres (pour {nights} nuits):</span>
              <span>{roomsTotal} €</span>
            </div>
            <div className="flex justify-between text-[#67273f]">
              <span>Services supplémentaires:</span>
              <span>{getTotalExtrasPrice()} €</span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg text-[#67273f]">
              <span>Total:</span>
              <span>{roomsTotal + getTotalExtrasPrice()} €</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <button
              onClick={handleBack}
              className="flex items-center justify-center px-6 py-3 border-2 border-[#67273f] text-[#67273f] font-semibold rounded-[10px]  transition-all duration-200 hover:bg-[#67273f] hover:text-white"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Retour
            </button>

            <button
              onClick={handleContinue}
              className="flex items-center justify-center px-6 py-3 bg-[#87764e] hover:bg-[#7a6b47] text-white font-semibold rounded-[10px]  transition-all duration-200"
            >
              Continuer
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtrasStep;
